import { useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ColDef, ICellRendererParams } from 'ag-grid-community';

import Table from '~/shared/components/Table';
import { useXFetch } from '~/shared/utils/useXFetch';
import {
  OptLogVolumeActionsCountResponse,
  OptLogVolumeActionsResponse,
  OptLogVolumeActions,
} from '~/shared/types/api';
import { TABLE_LIVE_REFRESH_MS } from '~/shared/constants/polling';
import { formatDate } from '~/shared/utils/formatNumber';
import OptimizationActions from '~/reports/components/ReportsPage/components/OptimizationActions';
import TypeCell from '~/reports/components/ReportTable/TypeCell';
import SizeCell from '~/shared/components/SizeCell';

interface VolumeActionsLogProps {
  tableQueryUrl: string;
  countQueryUrl: string;
  onPageChange: (page: number) => void;
  onSortChange: (sort: { field: string; dir: 'desc' | 'asc' }) => void;
  dataLimit: number;
  currentSortField: { field: string; dir: 'desc' | 'asc' };
  currentOffset: string;
  timeSignature: string;
  filtersSignature: string;
}

export default function VolumeActionsLog({
  tableQueryUrl,
  countQueryUrl,
  onPageChange,
  onSortChange,
  dataLimit,
  currentSortField,
  currentOffset,
  timeSignature,
  filtersSignature,
}: VolumeActionsLogProps) {
  const api = useXFetch();

  const { data, isFetching, isPlaceholderData, isLoading } = useQuery({
    queryKey: [
      'getOptimizationActions',
      tableQueryUrl,
      countQueryUrl,
      currentOffset,
      timeSignature,
      filtersSignature,
    ],
    refetchOnWindowFocus: false,
    enabled: !!tableQueryUrl && !!countQueryUrl,
    refetchInterval: TABLE_LIVE_REFRESH_MS,
    placeholderData: (old, prev) => {
      if (!old || !prev) {
        return undefined;
      }
      const prevTimeSignature = prev.queryKey[4] as string | undefined;
      const prevFiltersSignature = prev.queryKey[5] as string | undefined;
      return prevTimeSignature === timeSignature && prevFiltersSignature === filtersSignature
        ? old
        : undefined;
    },
    queryFn: async () => {
      const actionsResponse = await api<OptLogVolumeActionsResponse>(tableQueryUrl);
      const countResponse = await api<OptLogVolumeActionsCountResponse>(countQueryUrl);
      const actions = actionsResponse.actions ?? [];
      const count = countResponse.count ?? 0;
      return { actions, count };
    },
  });

  const columnDefs = useMemo(
    () => getVolumeActionsColumnDefs({ sortField: currentSortField }),
    [currentSortField]
  );

  const lastKnownCountRef = useRef<number | undefined>(undefined);
  if (data?.count !== undefined) {
    lastKnownCountRef.current = data.count;
  }
  const stableCount = lastKnownCountRef.current ?? 0;

  const currentPage = useMemo(
    () => Math.floor((parseInt(currentOffset, 10) || 0) / dataLimit),
    [currentOffset, dataLimit]
  );

  const showLoader = (isLoading && !data) || (isFetching && !data && !isPlaceholderData);

  return (
    <div className="flex flex-col gap-[14px] mt-4">
      <div>
        <OptimizationActions
          totalActionsCount={stableCount}
          totalActionsCountIsLoading={isLoading}
          growActions={0}
          shrinkActions={0}
        />
      </div>

      <div className="flex flex-col pb-[14px] h-[520px] 2xl:h-[702px] ">
        <Table<OptLogVolumeActions>
          columnDefs={columnDefs}
          rowData={data?.actions || []}
          onSortChange={onSortChange}
          pagination={{
            currentPage: currentPage,
            totalPages: Math.ceil(stableCount / dataLimit),
            dataCount: stableCount,
            perPage: dataLimit,
            onChange: (page: number) => onPageChange(page),
          }}
          loading={showLoader}
          itemsName="actions"
        />
      </div>
    </div>
  );
}

const getVolumeActionsColumnDefs = ({
  sortField,
}: {
  sortField: { field: string; dir: 'desc' | 'asc' };
}): ColDef<OptLogVolumeActions>[] => [
  {
    headerName: 'Type',
    flex: 2,
    minWidth: 200,
    maxWidth: 278,
    field: 'type',
    cellRenderer: (params: ICellRendererParams<OptLogVolumeActions>) => {
      const { data, node } = params;
      if (!data?.type) {
        return null;
      }
      const uniqueId = `${data.volumeId || 'vol'}-${data.actionFinishTime || node.rowIndex}`;
      return (
        <TypeCell
          key={uniqueId}
          type={data.type}
          volumeId={uniqueId}
          progress={data.progress}
          state={data.state}
        />
      );
    },
  },
  {
    headerName: 'Original Size',
    flex: 1,
    minWidth: 120,
    field: 'originalDiskSizeGb',
    cellRenderer: ({ data }: ICellRendererParams<OptLogVolumeActions>) =>
      data?.originalDiskSizeGb ? <SizeCell size={data.originalDiskSizeGb} sizeInGiB /> : null,
    sort: sortField?.field === 'originalDiskSizeGb' ? sortField?.dir : null,
  },
  {
    headerName: 'New Size',
    flex: 1,
    minWidth: 120,
    field: 'newDiskSizeGb',
    cellRenderer: ({ data }: ICellRendererParams<OptLogVolumeActions>) =>
      data?.newDiskSizeGb ? <SizeCell size={data.newDiskSizeGb} sizeInGiB /> : null,
    sort: sortField?.field === 'newDiskSizeGb' ? sortField?.dir : null,
  },
  {
    headerName: 'Start Time',
    flex: 1.2,
    minWidth: 140,
    field: 'actionStartTime',
    valueFormatter: ({ value }) => (value ? formatDate(value, true) : ''),
    sort: sortField?.field === 'actionStartTime' ? sortField?.dir : null,
    cellStyle: { fontWeight: 500, color: '#071031', fontSize: '14px', lineHeight: '20px' },
  },
  {
    headerName: 'End Time',
    flex: 1.2,
    minWidth: 140,
    field: 'actionFinishTime',
    valueFormatter: ({ data, value }) =>
      data?.state && ['Success', 'Failure', 'Canceled'].includes(data.state)
        ? formatDate(value, true)
        : '',
    sort: sortField?.field === 'actionFinishTime' ? sortField?.dir : null,
    cellStyle: { fontWeight: 500, color: '#071031', fontSize: '14px', lineHeight: '20px' },
  },
];
